/**
 * Memory Pressure Monitoring System
 * 
 * Monitors system memory usage and triggers automated responses
 * to prevent memory exhaustion and maintain system stability.
 */

import { MemorySizeEstimator } from './size-estimator'
import { Logger } from '@/utils/logger'

const logger = new Logger('MemoryPressureMonitor')

export interface MemoryPressureConfig {
  thresholdMB: number
  warningThresholdMB: number
  criticalThresholdMB: number
  checkInterval: number
  enableGC: boolean
  autoFlush: boolean
  adaptiveSizing: boolean
}

export interface MemoryPressureEvent {
  timestamp: number
  level: 'normal' | 'warning' | 'critical'
  heapUsedMB: number
  heapTotalMB: number
  rss: number
  external: number
  trigger: string
  actions: string[]
}

export interface MemoryPressureHandler {
  priority: number
  name: string
  handler: (event: MemoryPressureEvent) => Promise<boolean> | boolean
}

export class MemoryPressureMonitor {
  private config: MemoryPressureConfig
  private isRunning = false
  private interval?: NodeJS.Timeout
  private handlers: MemoryPressureHandler[] = []
  private lastPressureCheck = 0
  private pressureHistory: MemoryPressureEvent[] = []
  private consecutivePressureEvents = 0

  // Cooldown to prevent spam
  private lastWarningTime = 0
  private lastCriticalTime = 0
  private readonly WARNING_COOLDOWN = 30000 // 30 seconds
  private readonly CRITICAL_COOLDOWN = 10000 // 10 seconds

  constructor(config: MemoryPressureConfig) {
    this.config = { ...config }
    
    logger.info('Memory pressure monitor initialized', {
      thresholdMB: config.thresholdMB,
      warningMB: config.warningThresholdMB,
      criticalMB: config.criticalThresholdMB,
      checkInterval: config.checkInterval
    })
  }

  /**
   * Start monitoring memory pressure
   */
  start(): void {
    if (this.isRunning) {
      return
    }

    this.isRunning = true
    this.interval = setInterval(() => {
      this.checkMemoryPressure()
    }, this.config.checkInterval)

    logger.info('Memory pressure monitoring started')
  }

  /**
   * Stop monitoring
   */
  stop(): void {
    if (!this.isRunning) {
      return
    }

    this.isRunning = false
    
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = undefined
    }

    logger.info('Memory pressure monitoring stopped')
  }

  /**
   * Add pressure handler
   */
  addHandler(handler: MemoryPressureHandler): void {
    this.handlers.push(handler)
    
    // Sort by priority (higher priority first)
    this.handlers.sort((a, b) => b.priority - a.priority)
    
    logger.debug(`Added pressure handler: ${handler.name}`, {
      priority: handler.priority,
      totalHandlers: this.handlers.length
    })
  }

  /**
   * Remove pressure handler
   */
  removeHandler(name: string): boolean {
    const index = this.handlers.findIndex(h => h.name === name)
    if (index !== -1) {
      this.handlers.splice(index, 1)
      logger.debug(`Removed pressure handler: ${name}`)
      return true
    }
    return false
  }

  /**
   * Get current memory status
   */
  getCurrentStatus(): {
    level: 'normal' | 'warning' | 'critical'
    heapUsedMB: number
    heapTotalMB: number
    pressureRatio: number
    isUnderPressure: boolean
  } {
    const usage = process.memoryUsage()
    const heapUsedMB = usage.heapUsed / (1024 * 1024)
    const heapTotalMB = usage.heapTotal / (1024 * 1024)
    
    let level: 'normal' | 'warning' | 'critical' = 'normal'
    
    if (heapUsedMB >= this.config.criticalThresholdMB) {
      level = 'critical'
    } else if (heapUsedMB >= this.config.warningThresholdMB) {
      level = 'warning'
    }

    return {
      level,
      heapUsedMB,
      heapTotalMB,
      pressureRatio: heapUsedMB / this.config.thresholdMB,
      isUnderPressure: heapUsedMB >= this.config.thresholdMB
    }
  }

  /**
   * Get pressure history
   */
  getHistory(maxEvents: number = 100): MemoryPressureEvent[] {
    return this.pressureHistory.slice(-maxEvents)
  }

  /**
   * Force a memory check
   */
  async forceCheck(): Promise<MemoryPressureEvent | null> {
    return this.checkMemoryPressure()
  }

  /**
   * Get statistics
   */
  getStatistics(): {
    totalEvents: number
    warningEvents: number
    criticalEvents: number
    averageHeapUsed: number
    consecutivePressureEvents: number
  } {
    const warningEvents = this.pressureHistory.filter(e => e.level === 'warning').length
    const criticalEvents = this.pressureHistory.filter(e => e.level === 'critical').length
    const averageHeapUsed = this.pressureHistory.length > 0 ?
      this.pressureHistory.reduce((sum, e) => sum + e.heapUsedMB, 0) / this.pressureHistory.length :
      0

    return {
      totalEvents: this.pressureHistory.length,
      warningEvents,
      criticalEvents,
      averageHeapUsed,
      consecutivePressureEvents: this.consecutivePressureEvents
    }
  }

  /**
   * Check current memory pressure
   */
  private async checkMemoryPressure(): Promise<MemoryPressureEvent | null> {
    this.lastPressureCheck = Date.now()
    
    const usage = process.memoryUsage()
    const heapUsedMB = usage.heapUsed / (1024 * 1024)
    const heapTotalMB = usage.heapTotal / (1024 * 1024)
    
    let level: 'normal' | 'warning' | 'critical' = 'normal'
    let shouldTrigger = false
    let trigger = ''

    // Determine pressure level
    if (heapUsedMB >= this.config.criticalThresholdMB) {
      level = 'critical'
      shouldTrigger = this.shouldTriggerCritical()
      trigger = 'critical_memory_usage'
    } else if (heapUsedMB >= this.config.warningThresholdMB) {
      level = 'warning'
      shouldTrigger = this.shouldTriggerWarning()
      trigger = 'warning_memory_usage'
    } else if (heapUsedMB >= this.config.thresholdMB) {
      // Above threshold but not warning level
      trigger = 'threshold_exceeded'
      shouldTrigger = true
    }

    if (!shouldTrigger && level === 'normal') {
      // Reset consecutive counter on normal level
      this.consecutivePressureEvents = 0
      return null
    }

    // Create pressure event
    const event: MemoryPressureEvent = {
      timestamp: Date.now(),
      level,
      heapUsedMB,
      heapTotalMB,
      rss: usage.rss,
      external: usage.external,
      trigger,
      actions: []
    }

    if (shouldTrigger) {
      this.consecutivePressureEvents++
      
      // Execute handlers
      const actions = await this.executeHandlers(event)
      event.actions = actions
      
      // Update cooldown times
      if (level === 'warning') {
        this.lastWarningTime = Date.now()
      } else if (level === 'critical') {
        this.lastCriticalTime = Date.now()
      }
      
      // Add to history
      this.pressureHistory.push(event)
      
      // Keep history limited
      if (this.pressureHistory.length > 1000) {
        this.pressureHistory = this.pressureHistory.slice(-500)
      }
      
      logger.warn('Memory pressure detected', {
        level,
        heapUsedMB: Math.round(heapUsedMB * 100) / 100,
        threshold: this.config.thresholdMB,
        actions: actions.length,
        consecutive: this.consecutivePressureEvents
      })
      
      return event
    }

    return null
  }

  /**
   * Check if we should trigger warning (respects cooldown)
   */
  private shouldTriggerWarning(): boolean {
    return Date.now() - this.lastWarningTime >= this.WARNING_COOLDOWN
  }

  /**
   * Check if we should trigger critical (respects cooldown)
   */
  private shouldTriggerCritical(): boolean {
    return Date.now() - this.lastCriticalTime >= this.CRITICAL_COOLDOWN
  }

  /**
   * Execute all registered handlers
   */
  private async executeHandlers(event: MemoryPressureEvent): Promise<string[]> {
    const actions: string[] = []
    
    for (const handler of this.handlers) {
      try {
        const result = await handler.handler(event)
        if (result) {
          actions.push(handler.name)
        }
      } catch (error) {
        logger.error(`Pressure handler '${handler.name}' failed`, error)
      }
    }
    
    return actions
  }
}

/**
 * Default memory pressure handlers
 */
export const DefaultMemoryHandlers = {
  /**
   * Force garbage collection if available
   */
  forceGC: {
    priority: 100,
    name: 'force_gc',
    handler: (event: MemoryPressureEvent): boolean => {
      if (event.level === 'critical' && global.gc) {
        try {
          global.gc()
          logger.debug('Forced garbage collection')
          return true
        } catch (error) {
          logger.warn('Failed to force GC', error)
        }
      }
      return false
    }
  },

  /**
   * Log memory statistics
   */
  logStats: {
    priority: 10,
    name: 'log_memory_stats',
    handler: (event: MemoryPressureEvent): boolean => {
      const summary = MemorySizeEstimator.getMemorySummary()
      logger.info('Memory pressure statistics', {
        level: event.level,
        heap: {
          used: MemorySizeEstimator.formatBytes(summary.heapUsed),
          total: MemorySizeEstimator.formatBytes(summary.heapTotal)
        },
        rss: MemorySizeEstimator.formatBytes(summary.rss),
        external: MemorySizeEstimator.formatBytes(summary.external)
      })
      return true
    }
  }
}